import { onlineManager } from '@tanstack/react-query';
import type { ShouldRevalidateFunctionArgs } from 'react-router';
import { capturerPortee, estActuelle, type CapturePortee } from '../../auth/identite.js';
import { compteConfine } from '../../auth/confinement.js';
import type { DonneesCoquille } from '../loaders.js';

/**
 * La dernière page que la coquille a réellement AFFICHÉE : son adresse, les
 * données qu'elle affichait, et l'époque de session sous laquelle.
 */
let derniere: {
  readonly adresse: string;
  readonly donnees: DonneesCoquille;
  readonly portee: CapturePortee;
} | null = null;

/** Retour local demandé depuis la page d'erreur, consommé par le chargeur. */
let retourLocal: string | null = null;

export function memoriserPageAffichee(adresse: string, donnees: DonneesCoquille): void {
  derniere = { adresse, donnees, portee: capturerPortee() };
}

/**
 * L'adresse de la dernière page affichée, si elle appartient encore à
 * l'identité courante (même époque) — sinon rien : on ne revient jamais sur
 * la page d'un autre compte ou d'une autre galerie.
 */
export function derniereAdresseAffichee(): string | null {
  if (!derniere || !estActuelle(derniere.portee)) return null;
  return compteConfine(derniere.donnees.session.compte.id) ? null : derniere.adresse;
}

/** La page d'erreur demande de revenir à la dernière page affichée. */
export function demanderRetourLocal(): string | null {
  retourLocal = derniereAdresseAffichee();
  return retourLocal;
}

/**
 * Sortie LOCALE de la page d'erreur (relecture du 23/09, constat F) : sur la
 * demande explicite de l'utilisateur, et pour cette adresse seulement, le
 * chargeur réaffiche la dernière page de la MÊME identité (même époque) avec
 * les données qu'elle affichait déjà. Ce n'est pas un repli de session : rien
 * n'est lu d'un cache quand `/auth/me` échoue, aucune autre identité ne peut
 * passer, et la demande ne sert qu'une fois.
 */
export function consommerRetourLocal(url: string): DonneesCoquille | null {
  const demande = retourLocal;
  retourLocal = null;
  if (demande === null || derniere === null || !estActuelle(derniere.portee)) return null;
  // La session a depuis été confinée (CGU, double authentification) — la
  // page affichée avant ne revient pas.
  if (compteConfine(derniere.donnees.session.compte.id)) return null;
  const cible = new URL(url);
  if (`${cible.pathname}${cible.search}` !== demande || derniere.adresse !== demande) return null;
  return derniere.donnees;
}

/**
 * Hors ligne, une navigation DANS la même galerie ne relance pas le chargeur
 * de la coquille (constat F) : il redemanderait une session que le réseau ne
 * peut pas donner, et la page déjà chargée disparaîtrait derrière une erreur.
 * La coquille garde simplement la session qu'elle a déjà chargée. Tout
 * changement de galerie passe par le chargeur (et la coquille le bloque sans
 * réseau).
 */
export function coquilleDoitRevalider({
  currentParams,
  nextParams,
  defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs): boolean {
  if (!onlineManager.isOnline() && currentParams['slug'] === nextParams['slug']) return false;
  return defaultShouldRevalidate;
}
